import { useState } from 'react';
import { Alert, Button, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import type { Camera } from '../../app/types';
import PageSection from '../shared/PageSection';

interface CamerasPageProps {
  readonly tenantId: string;
  readonly cameras: Camera[];
  readonly onCreateCamera: (payload: { tenantId: string; name: string }) => Promise<void>;
  readonly onUploadSnapshot: (cameraId: string, file: File) => Promise<void>;
}

export default function CamerasPage({ tenantId, cameras, onCreateCamera, onUploadSnapshot }: CamerasPageProps) {
  const [cameraName, setCameraName] = useState('');
  const [uploadingCameraId, setUploadingCameraId] = useState('');
  const [error, setError] = useState('');

  const create = async () => {
    try {
      setError('');
      await onCreateCamera({ tenantId, name: cameraName });
      setCameraName('');
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Unable to create camera.');
    }
  };

  const handleFileChange = async (cameraId: string, fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) {
      return;
    }

    try {
      setUploadingCameraId(cameraId);
      setError('');
      await onUploadSnapshot(cameraId, file);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Unable to upload snapshot.');
    } finally {
      setUploadingCameraId('');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <PageSection title="Camera Registry" subtitle="Registered cameras for the active tenant scope.">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Upload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cameras.map((camera) => (
                <TableRow key={camera.cameraId}>
                  <TableCell>{camera.name}</TableCell>
                  <TableCell>{camera.status}</TableCell>
                  <TableCell>{new Date(camera.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button component="label" variant="outlined" size="small" disabled={uploadingCameraId === camera.cameraId}>
                      {uploadingCameraId === camera.cameraId ? 'Uploading...' : 'Upload Snapshot'}
                      <input hidden type="file" accept="image/*" onChange={(event) => void handleFileChange(camera.cameraId, event.target.files)} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PageSection>
      </Grid>
      <Grid item xs={12} md={5}>
        <PageSection title="Register Camera">
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField label="Camera name" value={cameraName} onChange={(event) => setCameraName(event.target.value)} />
            <Button variant="contained" onClick={() => void create()} disabled={!cameraName}>
              Add Camera
            </Button>
          </Stack>
        </PageSection>
      </Grid>
    </Grid>
  );
}
